// COP — in-process envelope bus.
//
// The HostContext.data surface (CONTRACT:C3-CAPABILITY-PLUGIN.1.0) is the only
// way a capability touches the envelope feed. In the real system this is the
// API gateway / event bus (CONTRACT:S5-API-GATEWAY.1.0 / CONTRACT:I3-EVENT-BUS.1.0);
// here it is a thin local fan-out: the feed publishes CanonicalEnvelopes, the
// capability subscribes (filtered by kind) and pushes them at the engine.

import type {
  CanonicalEnvelope,
  Disposable,
  EntityFilter,
} from "../../../../packages/contracts-ts/src/index.ts";

type Subscriber = {
  filter: EntityFilter;
  cb: (env: CanonicalEnvelope) => void;
};

/** Does an envelope match a filter? Open seam: matches by kind and/or id. */
function matches(filter: EntityFilter, env: CanonicalEnvelope): boolean {
  if (filter.kinds && filter.kinds.length > 0 && !filter.kinds.includes(env.kind)) {
    return false;
  }
  if (filter.ids && filter.ids.length > 0 && !filter.ids.includes(env.id)) {
    return false;
  }
  return true;
}

export class EnvelopeBus {
  private readonly subs = new Set<Subscriber>();

  /** HostServices.data.subscribe — returns a Disposable that detaches the sub. */
  subscribe(filter: EntityFilter, cb: (env: CanonicalEnvelope) => void): Disposable {
    const sub: Subscriber = { filter, cb };
    this.subs.add(sub);
    return {
      dispose: () => {
        this.subs.delete(sub);
      },
    };
  }

  /** HostServices.data.publish — fan out to matching subscribers. */
  publish(env: CanonicalEnvelope): Promise<void> {
    for (const sub of this.subs) {
      if (matches(sub.filter, env)) {
        try {
          sub.cb(env);
        } catch {
          // A bad subscriber must not break the bus; the host isolates crashes.
        }
      }
    }
    return Promise.resolve();
  }
}
